import React, { useCallback, useEffect, useState } from "react";
import { FaArrowLeft, FaDownload, FaRedo, FaSyncAlt, FaWhatsapp, FaEnvelope, FaUsers, FaUserSlash } from "react-icons/fa";
import DataTable from "../../../components/DataTable";
import KPICard from "../../../components/KPICard";
import StatusBadge from "../../../components/StatusBadge";
import { colors, spacing, typography, btnSecondary, btnGhost } from "../../../utils/designTokens";
import { comunicacionAdmin, mensajeDeError } from "../../../utils/comunicaciones/api";
import { ESTADOS, ESTADO_CANAL, MOTIVOS, sePuedeReenviar, formatoFecha } from "../../../utils/comunicaciones/contenido";

// Seguimiento de una comunicación enviada: por persona, el WhatsApp (enviado,
// entregado, leído) y el email; reenviar lo que no llegó y bajar el PDF.
// Props: id, onVolver(), showToast

function Canal({ c }) {
  const e = ESTADO_CANAL[(c && c.estado) || "omitido"] || ESTADO_CANAL.omitido;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <StatusBadge status={e.status} label={e.label} size="sm" />
      {c && c.motivo && MOTIVOS[c.motivo] && <span style={S.motivo}>{MOTIVOS[c.motivo]}</span>}
    </span>
  );
}

export default function ComunicacionEstado({ id, onVolver, showToast }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [reenviando, setReenviando] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await comunicacionAdmin("estado", { id }));
    } catch (e) {
      showToast && showToast(mensajeDeError(e), "error");
    } finally {
      setCargando(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const reenviar = async (fila) => {
    setReenviando(fila.userid);
    try {
      const e = await comunicacionAdmin("reenviar", { id, userid: fila.userid });
      showToast && showToast(e && e.wa && e.wa.estado === "pendiente" ? "Queda en cola: sale en el horario de envío (9 a 20 h)." : `Reenviado a ${fila.nombre || "la persona"}.`, "success");
      await cargar();
    } catch (err) {
      showToast && showToast(mensajeDeError(err), "error");
    } finally {
      setReenviando(null);
    }
  };

  const bajarPdf = async () => {
    try {
      const { url } = await comunicacionAdmin("pdf", { id });
      window.open(url, "_blank", "noopener");
    } catch (e) {
      showToast && showToast(mensajeDeError(e), "error");
    }
  };

  const c = datos && datos.comunicacion;
  const r = datos && datos.resumen;
  const estado = c ? ESTADOS[c.estado] || ESTADOS.enviada : null;
  const columnas = [
    { key: "nombre", label: "Persona", render: (v) => v || "—" },
    { key: "unidades", label: "Unidades", width: "140px", render: (v) => (v && v.length ? v.join(", ") : "—") },
    { key: "wa", label: "WhatsApp", width: "160px", render: (v) => <Canal c={v} /> },
    { key: "mail", label: "Email", width: "160px", render: (v) => <Canal c={v} /> },
    {
      key: "acciones",
      label: "",
      width: "120px",
      align: "right",
      render: (_, fila) =>
        sePuedeReenviar(fila) ? (
          <button
            type="button"
            style={{ ...btnGhost, padding: "6px 10px" }}
            className="habitia-btn-ghost"
            onClick={() => reenviar(fila)}
            disabled={reenviando === fila.userid}
            aria-label={`Reenviar a ${fila.nombre || "la persona"}`}
          >
            <FaRedo size={11} /> {reenviando === fila.userid ? "Enviando…" : "Reenviar"}
          </button>
        ) : null,
    },
  ];

  return (
    <div>
      <div style={S.cabecera}>
        <button type="button" style={{ ...btnGhost, padding: "8px 12px" }} className="habitia-btn-ghost" onClick={onVolver}>
          <FaArrowLeft size={12} /> Comunicaciones
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" style={{ ...btnGhost, padding: "8px 12px" }} className="habitia-btn-ghost" onClick={cargar} disabled={cargando}>
          <FaSyncAlt size={12} /> Actualizar
        </button>
        <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={bajarPdf}>
          <FaDownload size={12} /> Descargar PDF
        </button>
      </div>

      {c && (
        <div style={S.titulo}>
          <h2 style={S.h2}>
            Comunicación N° {c.numero} · {c.asunto}
          </h2>
          <div style={S.meta}>
            <StatusBadge status={estado.status} label={estado.label} size="sm" />
            <span>Enviada el {formatoFecha(c.enviadaEnMs)}</span>
            {c.dryRun && <StatusBadge status="dry_run" label="Modo prueba" size="sm" />}
          </div>
        </div>
      )}

      {r && (
        <div style={S.kpis}>
          <KPICard icon={FaUsers} iconBg={colors.primaryLighter} label="Personas" value={r.total} />
          <KPICard icon={FaWhatsapp} iconBg={colors.successLight} iconColor={colors.successDark} label="WhatsApp entregados" value={r.wa.entregado + r.wa.leido} badge={r.wa.leido ? `${r.wa.leido} leídos` : undefined} badgeColor="success" />
          <KPICard icon={FaEnvelope} iconBg={colors.infoLight} iconColor={colors.infoDark} label="Emails enviados" value={r.mail.enviado} />
          <KPICard icon={FaUserSlash} iconBg={colors.gray100} iconColor={colors.gray600} label="Sin contacto" value={r.sinContacto} />
        </div>
      )}
      {r && r.pendientes > 0 && <p style={S.hint}>Quedan {r.pendientes} envíos en cola: salen de 9 a 20 h y se reintentan hasta 3 veces.</p>}

      <DataTable
        columns={columnas}
        data={(datos && datos.envios) || []}
        emptyTitle={cargando ? "Cargando…" : "Sin destinatarios"}
      />
    </div>
  );
}

const S = {
  cabecera: { display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[4], flexWrap: "wrap" },
  titulo: { marginBottom: spacing[4] },
  h2: { margin: 0, fontSize: typography.sizes.xl, fontWeight: 400, color: colors.gray900 },
  meta: { display: "flex", alignItems: "center", gap: spacing[3], marginTop: spacing[2], fontSize: typography.sizes.sm, color: colors.gray600 },
  kpis: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4], marginBottom: spacing[4] },
  hint: { fontSize: typography.sizes.sm, color: colors.gray600, margin: `0 0 ${spacing[3]}px` },
  motivo: { fontSize: typography.sizes.xs, color: colors.gray600 },
};
